import React, { useState } from "react";
import { Tag, Check, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { apiClient } from "@/lib/api";
import { capture } from "@/lib/analytics";

const PLAN_LABEL = {
  mastery_monthly: "Monthly",
  mastery_annual: "Annual",
};

/**
 * Compact promo code redemption input.
 *
 * Props:
 *  - onApply(code, info): callback when a valid promo is applied. `info` includes
 *    { applies_to, final_amount, original_amount, saved, discount_type, discount_value, expires_at }
 *  - onClear(): callback when the user removes an applied promo
 *  - applied: { code, info } — controlled "applied" state from parent
 *
 * Validates against both `mastery_monthly` and `mastery_annual` so the user
 * sees which plans the code applies to. Mutually exclusive with referral
 * discounts — hidden if the signed-in user already has a referral credit.
 */
export default function PromoCodeInput({ onApply, onClear, applied, className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(!!applied);
  const [code, setCode] = useState(applied?.code || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Don't show promo input to users who already hold a referral discount —
  // referral rewards (90% off Monthly / 30% off Annual) supersede any promo
  // and cannot be stacked.
  const hasReferral =
    !!user?.referral_monthly_available || !!user?.referral_annual_available;
  if (hasReferral) return null;

  const validate = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy(true);
    setError(null);
    // Try both plans so we can tell the user which the code applies to.
    const plans = ["mastery_monthly", "mastery_annual"];
    let firstReason = null;
    let success = null;
    let appliesTo = [];
    for (const plan of plans) {
      try {
        const r = await apiClient.post("/promo/validate", { code: c, plan });
        if (!success) success = { plan, info: r.data };
        appliesTo.push(plan);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const reason =
          typeof detail === "object" && detail?.reason ? detail.reason : "not_found";
        if (!firstReason) firstReason = { reason, message: detail?.message || detail };
      }
    }
    setBusy(false);
    if (!success) {
      const reason = firstReason?.reason || "not_found";
      const message =
        firstReason?.message ||
        "That promo code isn't valid.";
      setError(message);
      capture("promo_validation_failed", {
        code_prefix: c.slice(0, 6),
        reason, // not_found | inactive | not_applicable | expired | limit_reached
      });
      // Dedicated events the redesign called for, fired with full context.
      if (reason === "expired") capture("promo_expired", { code_prefix: c.slice(0, 6) });
      if (reason === "limit_reached") capture("promo_limit_reached", { code_prefix: c.slice(0, 6) });
      return;
    }
    const info = { ...success.info, applies_to_plans: appliesTo };
    capture("promo_validated_ok", {
      code_prefix: c.slice(0, 6),
      discount_type: info.discount_type,
      discount_value: info.discount_value,
      applies_to: info.applies_to,
    });
    onApply?.(c, info);
    setError(null);
  };

  const clear = () => {
    onClear?.();
    setCode("");
    setError(null);
  };

  // ------ Applied state (parent confirmed the promo is in effect) ------
  if (applied?.info) {
    const info = applied.info;
    const appliesText =
      info.applies_to === "both"
        ? "Monthly & Annual"
        : PLAN_LABEL[info.applies_to] || info.applies_to;
    const isFree = Number(info.final_amount) === 0;
    const reward = isFree
      ? "First period free"
      : info.discount_type === "percent"
      ? `${Number(info.discount_value)}% off`
      : info.discount_type === "fixed_price"
      ? `First month $${Number(info.discount_value).toFixed(2)}`
      : `$${Number(info.discount_value).toFixed(2)} off`;
    return (
      <div
        className={`card-base p-4 ${className}`}
        style={{
          background: "linear-gradient(135deg, rgba(16,185,129,0.08), rgba(37,99,235,0.06))",
          borderColor: "rgba(16,185,129,0.4)",
        }}
        data-testid="promo-applied-card"
      >
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--emerald, #10B981)" }}
          >
            <Check size={18} color="#fff" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="font-mono text-sm font-semibold uppercase tracking-wider"
                data-testid="promo-applied-code"
              >
                {applied.code}
              </span>
              <span className="pill pill-emerald" style={{ fontSize: "0.7rem" }}>
                Applied
              </span>
            </div>
            <div className="text-sm text-[var(--ink)] mt-1" data-testid="promo-applied-reward">
              {reward} · Valid on {appliesText}
            </div>
            {info.expires_at && (
              <div className="text-xs text-[var(--ink-3)] mt-0.5" data-testid="promo-applied-expires">
                Expires {new Date(info.expires_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <button
            onClick={clear}
            className="text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors p-1"
            aria-label="Remove promo code"
            data-testid="promo-clear"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  // ------ Collapsed CTA ------
  if (!open) {
    return (
      <div className={className} data-testid="promo-code-input-container">
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center min-h-[44px] gap-1.5 text-sm font-semibold text-[var(--primary)] hover:underline px-2 py-2"
          data-testid="promo-code-toggle"
        >
          <Tag size={14} /> Have a promo code?
        </button>
      </div>
    );
  }

  // ------ Open input ------
  return (
    <div className={`card-base p-4 ${className}`} data-testid="promo-code-input-container">
      <div className="flex items-center gap-2 mb-2">
        <Tag size={14} className="text-[var(--primary)]" />
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-2)]">
          Apply a promo code
        </span>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") validate();
          }}
          className="brut-input flex-1 font-mono uppercase text-sm"
          placeholder="PROMPT100"
          autoFocus
          spellCheck={false}
          maxLength={40}
          data-testid="promo-code-input"
        />
        <button
          onClick={validate}
          disabled={busy || !code.trim()}
          className="btn-primary whitespace-nowrap inline-flex items-center min-h-[44px]"
          style={{ padding: "0.6rem 1.1rem", fontSize: "0.85rem" }}
          data-testid="promo-code-apply"
        >
          {busy ? <span className="spinner" /> : "Apply"}
        </button>
      </div>
      {error && (
        <div
          className="text-xs mt-2 leading-relaxed"
          style={{ color: "var(--accent, #FF5C2A)" }}
          data-testid="promo-code-error"
        >
          {error}
        </div>
      )}
      {!error && (
        <p className="text-xs text-[var(--ink-3)] mt-2">
          Reusable codes work on AI Mastery plans. Can't be combined with referral rewards.
        </p>
      )}
    </div>
  );
}
